import { useMemo, useState } from 'react'
import { motion } from 'framer-motion'
import { Check, CheckCheck, X, Clock, AlertTriangle, User, Pin } from 'lucide-react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { Message } from '@/contexts/ChatContext'
import { formatTime } from '@/lib/utils'
import { clsx } from 'clsx'
import { ChatChart } from './chat/ChatChart'
import { ArtifactPreviewCard } from './chat/ArtifactPreviewCard'
import { PageSuggestionBubble } from './chat/PageSuggestionBubble'

interface MessageBubbleProps {
  message: Message
  onPin?: () => void
}

/**
 * Full Markdown rendering for bot messages via react-markdown + remark-gfm.
 *
 * 2026-04-20 V2 chat-fault C fix: the prior hand-rolled renderer only
 * handled **bold** + numbered lists. The YoY and many other agent templates
 * emit GFM pipe-tables (`| Metric | ... |`), which rendered as raw text
 * with pipe characters — Brian's complaint "still not side by side or
 * table" on the live NOS session. remark-gfm enables tables, strikethrough,
 * autolinks, and task-lists. See project_v1_chat_failure_mode_diagnosis_20260420.md.
 *
 * Styling is class-scoped via custom component overrides so the output
 * stays visually aligned with the Butler chat bubble (outdoor contrast,
 * no default browser margins, compact table density).
 */
const MARKDOWN_COMPONENTS: Parameters<typeof ReactMarkdown>[0]['components'] = {
  // Keep paragraph spacing tight inside a chat bubble.
  p: ({ children }) => <p className="mb-2 last:mb-0">{children}</p>,
  // Tables — the core of this fix. Use semantic <table> with compact
  // styling; horizontal scroll on narrow screens so numbers never wrap
  // mid-cell. Zebra stripe on odd rows for quick scan.
  table: ({ children }) => (
    <div className="my-2 overflow-x-auto">
      <table className="min-w-full text-xs border-collapse">
        {children}
      </table>
    </div>
  ),
  thead: ({ children }) => (
    <thead className="border-b border-slate-300 dark:border-slate-600">
      {children}
    </thead>
  ),
  th: ({ children, style }) => (
    <th
      className="px-2 py-1 text-left font-semibold text-slate-700 dark:text-slate-200"
      style={style}
    >
      {children}
    </th>
  ),
  td: ({ children, style }) => (
    <td className="px-2 py-1 align-top border-t border-slate-200 dark:border-slate-700" style={style}>
      {children}
    </td>
  ),
  tr: ({ children }) => <tr className="odd:bg-slate-50 dark:odd:bg-slate-700/40">{children}</tr>,
  // Lists.
  ol: ({ children }) => <ol className="list-decimal list-inside space-y-0.5 my-1">{children}</ol>,
  ul: ({ children }) => <ul className="list-disc list-inside space-y-0.5 my-1">{children}</ul>,
  li: ({ children }) => <li className="text-sm leading-relaxed">{children}</li>,
  // Inline + code.
  code: ({ children, className }) => {
    const isBlock = className?.includes('language-')
    if (isBlock) {
      return (
        <pre className="bg-slate-100 dark:bg-slate-900 rounded p-2 my-2 overflow-x-auto text-xs">
          <code>{children}</code>
        </pre>
      )
    }
    return (
      <code className="bg-slate-100 dark:bg-slate-700 px-1 py-0.5 rounded text-xs">
        {children}
      </code>
    )
  },
  // Headings are rare in chat but keep them sensible when they appear.
  h1: ({ children }) => <h1 className="text-base font-bold mt-2 mb-1">{children}</h1>,
  h2: ({ children }) => <h2 className="text-sm font-bold mt-2 mb-1">{children}</h2>,
  h3: ({ children }) => <h3 className="text-sm font-semibold mt-1 mb-1">{children}</h3>,
  // Emphasis.
  strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
  // Defensive: the agent should never emit images or raw HTML; react-markdown
  // is configured to drop HTML by default, and no allowlist for <img> means
  // they'd need `allowElement` to pass through. Safe as-is.
}

function renderBotContent(text: string) {
  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={MARKDOWN_COMPONENTS}
    >
      {text}
    </ReactMarkdown>
  )
}

export function MessageBubble({ message, onPin }: MessageBubbleProps) {
  const [pinned, setPinned] = useState(false)
  const isUser = message.type === 'user'
  const isSystem = message.type === 'system'
  const isBot = message.type === 'bot'
  const isEscalated = message.metadata?.escalated
  const priority = message.metadata?.priority

  const getStatusIcon = () => {
    switch (message.status) {
      case 'sending':
        return <Clock className="w-3 h-3 text-slate-400" />
      case 'sent':
        return <Check className="w-3 h-3 text-slate-500" />
      case 'delivered':
        return <CheckCheck className="w-3 h-3 text-carnival-500" />
      case 'failed':
        return <X className="w-3 h-3 text-red-500" />
      default:
        return null
    }
  }

  const getPriorityIndicator = () => {
    if (!priority || priority === 'low') return null

    const colors = {
      medium: 'text-yellow-500',
      high: 'text-orange-500',
      urgent: 'text-red-500'
    }

    return (
      <AlertTriangle className={clsx('w-3 h-3', colors[priority])} />
    )
  }

  const renderedContent = useMemo(() => {
    if (isBot) return renderBotContent(message.content)
    return message.content
  }, [message.content, isBot])

  return (
    <motion.div
      layout
      className={clsx(
        'flex w-full',
        isUser ? 'justify-end' : 'justify-start'
      )}
    >
      <div className={clsx(
        'flex items-end space-x-2 max-w-[85%] sm:max-w-[75%]',
        isUser && 'flex-row-reverse space-x-reverse'
      )}>
        {/* Avatar */}
        {!isUser && !isSystem && (
          <motion.div
            whileHover={{ scale: 1.1 }}
            className="flex-shrink-0 w-8 h-8 bg-carnival-400 rounded-full flex items-center justify-center shadow-outdoor-sm"
          >
            <span className="text-white text-sm font-bold">🤖</span>
          </motion.div>
        )}

        {/* Message Content */}
        <div className="flex flex-col">
          <motion.div
            whileHover={{ scale: 1.01 }}
            className={clsx(
              'px-4 py-3 rounded-2xl shadow-outdoor-sm backdrop-blur-sm border',
              {
                // User messages
                'bg-carnival-400 text-carnival-900 border-carnival-300': isUser,
                // Bot messages
                'bg-white/90 dark:bg-slate-800/90 text-slate-800 dark:text-slate-100 border-slate-200 dark:border-slate-600': !isUser && !isSystem,
                // System messages
                'bg-blue-50 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200 border-blue-200 dark:border-blue-700': isSystem && !isEscalated,
                // Escalated system messages
                'bg-red-50 dark:bg-red-900/30 text-red-800 dark:text-red-200 border-red-200 dark:border-red-700': isSystem && isEscalated,
                // Rounded corners based on position
                'rounded-br-lg': isUser,
                'rounded-bl-lg': !isUser && !isSystem,
                'rounded-lg': isSystem
              }
            )}
          >
            {/* Priority indicator for system messages */}
            {isSystem && (priority === 'high' || priority === 'urgent') && (
              <div className="flex items-center space-x-1 mb-2">
                {getPriorityIndicator()}
                <span className="text-xs font-medium uppercase tracking-wide">
                  {priority} Priority
                </span>
              </div>
            )}

            {/* Message text */}
            <div className={clsx(
              'text-sm leading-relaxed outdoor-contrast',
              isUser && 'font-medium'
            )}>
              {renderedContent}
              {/* Streaming cursor */}
              {message.isStreaming && (
                <motion.span
                  className="inline-block w-1.5 h-4 ml-0.5 bg-carnival-500 rounded-sm align-middle"
                  animate={{ opacity: [1, 0] }}
                  transition={{ duration: 0.6, repeat: Infinity, ease: 'steps(1)' }}
                />
              )}
            </div>

            {/* V2d chart — sibling of markdown text. Only renders for bot
                messages that carry a structured chart payload, and only
                after the stream has finished (charts arrive on the `done`
                event, so mid-stream the field is absent by design). */}
            {isBot && !message.isStreaming && message.chart ? (
              <ChatChart chart={message.chart} density="compact" />
            ) : null}

            {/* V3 Page 07 — draft ArtifactSpec preview + pin suggestion.
                Rendered after the text + chart for bot messages only,
                after the stream finishes. Both fields arrive together
                on the `done` envelope when CHAT_PIN_SIDEBAR is on and
                the turn classified as an artifact-producing intent. */}
            {isBot && !message.isStreaming && message.artifactPreview ? (
              <ArtifactPreviewCard
                spec={message.artifactPreview.spec_draft}
                suggestedLabel={message.artifactPreview.suggested_label}
                suggestedGroup={message.artifactPreview.suggested_group}
                sourceTurnId={message.artifactPreview.source_turn_id}
              />
            ) : null}
            {isBot && !message.isStreaming && message.pageSuggestion ? (
              <PageSuggestionBubble
                suggestion={message.pageSuggestion}
                highlighted={message.pageSuggestion.explicit_pin}
              />
            ) : null}

            {/* Quick replies preview */}
            {message.quickReplies && message.quickReplies.length > 0 && (
              <div className="mt-3 pt-3 border-t border-current opacity-50">
                <div className="text-xs text-current">
                  Quick replies available below
                </div>
              </div>
            )}
          </motion.div>

          {/* Timestamp and Status */}
          <div className={clsx(
            'flex items-center space-x-1 mt-1 text-xs text-slate-500 dark:text-slate-400',
            isUser ? 'justify-end' : 'justify-start'
          )}>
            {!message.isStreaming && <span>{formatTime(message.timestamp)}</span>}
            {isUser && getStatusIcon()}
            {isSystem && message.metadata?.escalated && (
              <User className="w-3 h-3 text-carnival-500" aria-label="Human agent" />
            )}
            {isBot && !message.isStreaming && onPin && !pinned && (
              <button
                type="button"
                onClick={() => {
                  onPin()
                  setPinned(true)
                }}
                aria-label="Pin to Intelligence"
                className="ml-1 p-0.5 rounded hover:bg-butler-50 text-slate-400 hover:text-butler-600 transition-colors"
              >
                <Pin className="w-3 h-3" />
              </button>
            )}
            {isBot && pinned && (
              <span className="ml-1 flex items-center gap-0.5 text-butler-500">
                <Pin className="w-3 h-3" />
                <span className="text-[10px]">Pinned</span>
              </span>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  )
}
